<template>
  <div class="grid">
    <div class="content--wrapper">
      <h1>Crea el contenido de tu plantilla</h1>
      <p>
        Puedes añadir parámetros escribiendo
        {{ paramString() }} y colocando un número que indique el orden. Por ejemplo: Hola
        {{ paramString(1) }}, gracias por contactarnos por {{ paramString(2) }}.
      </p>

      <a-form layout="vertical" class="form" ref="formRef" :model="formState">
        <a-form-item label="Contenido de la plantilla" required>
          <a-textarea
            :showCount="false"
            :autoSize="{ minRows: 7, maxRows: 12 }"
            v-model:value="formState.content"
            placeholder="Hola {{1}}, gracias por contactarnos por {{2}}"
            @keydown="handleKeydown"
            @paste="handlePaste"
          />
          <div style="text-align: right; margin-top: 4px; color: rgb(172 172 172); font-size: 14px">
            <span>{{ customCount }} / {{ MAX_LENGTH }}</span>
          </div>
        </a-form-item>
      </a-form>

      <a-alert type="info" v-if="templateType !== WhatsappTemplateTypes.Text">
        <template #message>
          <h4 class="alert__title">
            {{ typeMessage.title }}
          </h4>
        </template>

        <template #description>
          <p class="alert__description" v-for="text in typeMessage.content" :key="text">
            {{ text }}
          </p>
        </template>
      </a-alert>
    </div>

    <WhatsappTemplatePhone
      style="place-self: center"
      :content="formattedContent"
      preview
      :templateType="templateType"
      :templateButton="templateButton"
    />
  </div>
</template>

<script>
import { defineComponent, reactive, ref, watch, computed, inject, nextTick } from "vue";

import { templateCategoriesOptions } from "@/app/channels/utils/selectOptions";
import { WhatsappTemplateTypes } from "@/app/channels/utils/enums";

import WhatsappTemplatePhone from "@/app/shared/components/organisms/WhatsappTemplatePhone.vue";

const paramString = (num = "") => `{{${num}}}`;

export default defineComponent({
  components: {
    WhatsappTemplatePhone,
  },
  setup(props, { emit, expose }) {
    const MAX_LENGTH = 1024;
    const TEMPLATE_DEFAULT_TEXT = "Aquí se visualizará tu plantilla de WhatsApp";

    const formRef = ref(null);

    const templateType = inject("templateType");
    const templateButton = inject("templateButton");

    const typeMessage = computed(() => {
      const message = {
        title: "Formato y tamaño aceptados para la carga de ",
        content: [],
      };

      if (templateType.value === WhatsappTemplateTypes.TextAndImage) {
        message.title += "imágenes";
        message.content.push("JPG, JPEG, PNG (2MB Max)");
        message.content.push("Las dimensiones ideales son 500 x 500");
      }

      if (templateType.value === WhatsappTemplateTypes.TextAndVideo) {
        message.title += "videos";
        message.content.push("MP4 (5MB Max)");
      }

      if (templateType.value === WhatsappTemplateTypes.TextAndDocument) {
        message.title += "documentos";
        message.content.push("PDF (5MB Max)");
      }

      return message;
    });

    const formState = inject("formStateContent");
    const valid = ref(false);
    watch(formState, () => {
      if (formState.content) { 
        valid.value = true; 
        return emit("active-button")
      };
      valid.value = false;
      return emit("inactive-button");
    });

    expose({ formState , valid});

    const customCount = computed(() => formState.content?.length || 0);

    //Da el formato para la previsualización
    const formattedContent = computed(() => {
      if (!formState.content) return TEMPLATE_DEFAULT_TEXT;

      return formState.content
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
        .replace(/_(.*?)_/g, "<em>$1</em>")
        .replace(/~(.*?)~/g, "<del>$1</del>")
        .replace(/```(.*?)```/g, "<code>$1</code>")
        .replace(/\n/g, "<br>");
    });

    const handleKeydown = (e) => {
      const textarea = e.target;

      const allowedKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
        "Enter",
      ];

      if (formState.content.length >= MAX_LENGTH) {
        if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
        }
      }
    };

    const handlePaste = (e) => {
      const paste = e.clipboardData.getData("text");

      const available = MAX_LENGTH - formState.content.length;
      if (available <= 0) {
        e.preventDefault();
        return;
      }

      if (paste.length > available) {
        e.preventDefault();

        const trimmed = paste.slice(0, available);

        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;

        const newValue =
          formState.content.slice(0, start) +
          trimmed +
          formState.content.slice(end);

        formState.content = newValue;
      }
    };

    return {
      WhatsappTemplateTypes,
      paramString,
      handleKeydown,
      handlePaste,
      templateCategoriesOptions,
      formRef,
      templateType,
      typeMessage,
      formState,
      templateButton,
      customCount,
      formattedContent,
      TEMPLATE_DEFAULT_TEXT,
      MAX_LENGTH,
    };
  },
});
</script>

<style lang="sass" scoped>
.grid
  display: grid
  grid-template-columns: 1.1fr 0.9fr
  gap: 25px

h3
  font-size: 16px
  font-weight: 600

p
  color: $gray-8

.content
  min-height: 254px
  max-height: 254px

  &--wrapper
    padding-top: 8px

.form
  margin-bottom: 45px

.alert
  &__title
    color: $gray-8
    font-size: 12px
    font-weight: 600
    margin: 0

  &__description
    color: $gray-8
    font-size: 12px
    margin: 0
</style>
